import { ChangeDetectionStrategy, Component, effect, inject, Input, signal, untracked, ViewChild } from '@angular/core';
import { forkJoin } from 'rxjs';
import { NotificationsService } from '../../../../core/services/notifications.service';
import { NotificationModel, NotificationsRequest } from '../../../../core/models/notification.model';
import { CommonModule } from '@angular/common';
import {
  AioButtonComponent,
  AioNotificationItemDirective,
  AioNotificationMenuComponent,
  type AioNotificationMenuGroup,
} from '@aio/ui';
import { DatePipe } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { ActionFeedbackService } from '../../../../core/services/action-feedback.service';
import { NotificationRedirectService } from '../../../../core/services/notification-redirect.service';
import { NotificationsPollService } from '../../../../core/services/notifications-poll.service';
import { extractTenantIdFromRoute } from '../../../../shared/utils/route.util';
import { getInitialsColor } from '../../../../shared/utils/initials.util';

/** Popover inbox stream page size (API max 100 per request). */
const NOTIFICATION_INBOX_PAGE_SIZE = 6;

interface NotificationMenuItem {
  id: string;
  unread: boolean;
  notification: NotificationModel;
}

@Component({
  selector: 'app-notifications',
  imports: [
    CommonModule,
    AioButtonComponent,
    AioNotificationMenuComponent,
    AioNotificationItemDirective,
  ],
  providers: [
    DatePipe
  ],
  templateUrl: './notifications.html',
  styleUrl: './notifications.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Notifications {

  @Input() showNotifications: boolean = false;

  @ViewChild('notificationMenu') notificationMenu?: AioNotificationMenuComponent;

  private notificationsService = inject(NotificationsService);
  private datePipe = inject(DatePipe);
  private sanitizer = inject(DomSanitizer);
  private router = inject(Router);
  private actionFeedback = inject(ActionFeedbackService);
  private notificationRedirect = inject(NotificationRedirectService);
  readonly notificationsPoll = inject(NotificationsPollService);

  isLoading = signal<boolean>(false);
  isLoadingPaginated = signal<boolean>(false);
  isMarkingAsRead = signal<boolean>(false);
  notifications: NotificationModel[] = [];
  todayNotifications = signal<NotificationModel[]>([]);
  yesterdayNotifications = signal<NotificationModel[]>([]);
  pastNotifications = signal<NotificationModel[]>([]);
  hasNextPage = signal<boolean>(true);

  /** True while the notification popover overlay is visible. */
  private readonly popoverOpen = signal(false);

  /** 1-based page cursor for inbox-ordered pagination. */
  private currentPageNumber = 1;

  /** Set when a poll/open refresh arrives while the first page is still loading. */
  private pendingRefresh = false;

  /** Prevents overlapping read-state sync requests while the popover stays open. */
  private isSyncingReadStates = false;

  constructor() {
    effect(() => {
      if (this.notificationsPoll.lastPollAt() === 0) {
        return;
      }
      // Only lastPollAt should re-run this effect; popoverOpen is read without tracking.
      if (!untracked(() => this.popoverOpen())) {
        return;
      }
      // Badge counts use unread-count poll; while open, merge read-state only so "See More" pages are kept.
      this.syncLoadedNotificationReadStates();
    });
  }

  /** Reload the first page when the user opens the popover. Not blocked by hasNextPage. */
  refreshNotificationList(): void {
    if (!this.showNotifications) {
      return;
    }
    if (this.isLoading()) {
      this.pendingRefresh = true;
      return;
    }

    this.pendingRefresh = false;
    this.currentPageNumber = 1;
    this.isLoading.set(true);
    this.notificationsService.getNotifications({
      pageNumber: 1,
      pageSize: NOTIFICATION_INBOX_PAGE_SIZE,
      sortBy: 'inbox',
    }).subscribe({
      next: (response) => {
        this.notifications = [];
        response.data.items.forEach(notification => {
          this.notifications.push({
            ...notification,
            sanitizedMessage: this.formatAndSanitizeMessage(notification),
            timestamp: this.formatTimestamp(notification.dateCreated)
          });
        });
        this.groupNotifications();
        this.hasNextPage.set(response.data.hasNextPage);
      },
      error: (error) => {
        console.error('❌ Error getting notifications:', error);
        this.actionFeedback.showError('Could not load notifications.');
      },
      complete: () => {
        this.isLoading.set(false);
        if (this.pendingRefresh) {
          this.pendingRefresh = false;
          this.refreshNotificationList();
        }
      }
    });
  }

  /**
   * Merge server read-state into the loaded popover list (badge poll / approval action read-clear)
   * without resetting paginated rows.
   */
  private syncLoadedNotificationReadStates(): void {
    if (
      !this.showNotifications
      || this.notifications.length === 0
      || this.isLoading()
      || this.isLoadingPaginated()
      || this.isSyncingReadStates
    ) {
      return;
    }

    this.isSyncingReadStates = true;
    const loadedIds = new Set(this.notifications.map((n) => n.notificationId));
    const pageRequests = Array.from({ length: this.currentPageNumber }, (_, index) =>
      this.notificationsService.getNotifications({
        pageNumber: index + 1,
        pageSize: NOTIFICATION_INBOX_PAGE_SIZE,
        sortBy: 'inbox',
      })
    );

    forkJoin(pageRequests).subscribe({
      next: (responses) => {
        const readStateById = new Map<number, boolean>();
        for (const response of responses) {
          for (const item of response.data?.items ?? []) {
            if (loadedIds.has(item.notificationId)) {
              readStateById.set(item.notificationId, item.isRead);
            }
          }
        }

        if (readStateById.size === 0) {
          return;
        }

        let changed = false;
        this.notifications = this.notifications.map((notification) => {
          const isRead = readStateById.get(notification.notificationId);
          if (isRead === undefined || isRead === notification.isRead) {
            return notification;
          }
          changed = true;
          return { ...notification, isRead };
        });

        if (changed) {
          this.groupNotifications();
        }
      },
      error: (error) => {
        console.error('❌ Error syncing notification read states:', error);
      },
      complete: () => {
        this.isSyncingReadStates = false;
      },
    });
  }

  getNotificationsPaginated(): void {
    if (
      this.isLoadingPaginated()
      || this.isLoading()
      || !this.hasNextPage()
      || !this.showNotifications
    ) {
      return;
    }

    this.isLoadingPaginated.set(true);
    const nextPageNumber = this.currentPageNumber + 1;
    const payload: NotificationsRequest = {
      pageNumber: nextPageNumber,
      pageSize: NOTIFICATION_INBOX_PAGE_SIZE,
      sortBy: 'inbox',
    };
    this.notificationsService.getNotifications(payload).subscribe({
      next: (response) => {
        const loadedIds = new Set(this.notifications.map(n => n.notificationId));
        response.data.items.forEach(notification => {
          if (loadedIds.has(notification.notificationId)) {
            return;
          }
          loadedIds.add(notification.notificationId);
          this.notifications.push({
            ...notification,
            sanitizedMessage: this.formatAndSanitizeMessage(notification),
            timestamp: this.formatTimestamp(notification.dateCreated)
          });
        });
        this.currentPageNumber = nextPageNumber;
        this.groupNotifications();
        this.hasNextPage.set(response.data.hasNextPage);
      },
      error: (error) => {
        console.error('❌ Error getting notifications:', error);
        this.actionFeedback.showError('Could not load notifications.');
      },
      complete: () => {
        this.isLoadingPaginated.set(false);
      }
    });
  }

  private formatAndSanitizeMessage(notification: NotificationModel): SafeHtml {
    const message = notification.message?.trim() ?? '';
    const actorName = notification.createdByUserName?.trim() ?? '';

    if (!message) {
      const title = notification.title?.trim() ?? '';
      if (!title) {
        return this.sanitizer.bypassSecurityTrustHtml('');
      }
      return this.sanitizer.bypassSecurityTrustHtml(this.emphasizeQuotedText(title));
    }

    const actorSuffix = actorName
      ? ` by <strong>${actorName}</strong>`
      : '';
    return this.sanitizer.bypassSecurityTrustHtml(
      `${this.emphasizeQuotedText(message)}${actorSuffix}`
    );
  }

  private emphasizeQuotedText(text: string): string {
    return text.replace(/"([^"]*)"/g, '<strong>$1</strong>');
  }

  private groupNotifications(): void {
    const utcNow = new Date();
    const utcTodayStart = Date.UTC(utcNow.getUTCFullYear(), utcNow.getUTCMonth(), utcNow.getUTCDate());
    const utcYesterdayStart = utcTodayStart - 86_400_000;

    const today: NotificationModel[] = [];
    const yesterday: NotificationModel[] = [];
    const past: NotificationModel[] = [];

    for (const notification of this.notifications) {
      const notifDate = this.parseUtcDate(notification.dateCreated);
      const notifDayStart = Date.UTC(
        notifDate.getUTCFullYear(),
        notifDate.getUTCMonth(),
        notifDate.getUTCDate()
      );

      if (notifDayStart >= utcTodayStart) {
        today.push(notification);
      } else if (notifDayStart >= utcYesterdayStart) {
        yesterday.push(notification);
      } else {
        past.push(notification);
      }
    }

    this.todayNotifications.set(today);
    this.yesterdayNotifications.set(yesterday);
    this.pastNotifications.set(past);
  }

  private parseUtcDate(dateCreated: string): Date {
    const trimmed = dateCreated.trim();
    if (!trimmed) {
      return new Date(NaN);
    }
    const hasTimezone = /(?:Z|[+-]\d{2}:\d{2})$/i.test(trimmed);
    return new Date(hasTimezone ? trimmed : `${trimmed}Z`);
  }

  private formatTimestamp(backendDate: string): string {
    const now = new Date();
    const past = this.parseUtcDate(backendDate);

    const diffMs = now.getTime() - past.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHr = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHr / 24);

    if (diffSec < 60) {
      return 'Just Now';
    } else if (diffMin < 60) {
      return `${diffMin} min ago`;
    } else if (diffMin == 60 || diffHr == 1) {
      return `an hour ago`;
    } else if (diffHr < 24) {
      return `${diffHr} hrs ago`;
    } else {
      if (past.getFullYear() === now.getFullYear()) {
        return this.datePipe.transform(past, 'MMM dd \'at\' hh:mm a')!;
      } else {
        return this.datePipe.transform(past, 'MMM dd, yyyy \'at\' hh:mm a')!;
      }
    }
  }

  statusLabel(): string {
    return `${this.notificationsPoll.effectiveGlobalUnreadCount()} New`;
  }

  menuGroups(): AioNotificationMenuGroup[] {
    const toItem = (notification: NotificationModel): NotificationMenuItem => ({
      id: String(notification.notificationId),
      unread: !notification.isRead,
      notification,
    });
    const groups: Array<AioNotificationMenuGroup & { items: NotificationMenuItem[] }> = [];
    const today = this.todayNotifications();
    const yesterday = this.yesterdayNotifications();
    const past = this.pastNotifications();
    if (today.length) groups.push({ id: 'today', title: 'Today', items: today.map(toItem) });
    if (yesterday.length) groups.push({ id: 'yesterday', title: 'Yesterday', items: yesterday.map(toItem) });
    if (past.length) groups.push({ id: 'past', title: 'Past', items: past.map(toItem) });
    return groups;
  }

  onItemPressed(id: string): void {
    const notification = this.notifications.find((item) => String(item.notificationId) === id);
    if (notification) this.markAsRead(notification);
  }

  onNotificationPopoverShow(): void {
    this.popoverOpen.set(true);
    this.refreshNotificationList();
  }

  onNotificationPopoverHide(): void {
    this.popoverOpen.set(false);
  }

  generateAvatarColor(name: string): string {
    return getInitialsColor(name);
  }

  generateInitials(name: string): string {
    if (!name) return '';

    const words = name.trim().split(/\s+/);
    if (words.length === 1) {
      return words[0].substring(0, 2).toUpperCase();
    }

    return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
  }

  markAsRead(notification: NotificationModel): void {
    if (this.isMarkingAsRead()) return;

    if (notification.isRead) {
      this.redirectToNotification(notification);
      return;
    }

    this.isMarkingAsRead.set(true);
    this.notificationsService.markAsRead(notification.notificationId).subscribe({
      next: () => {
        notification.isRead = true;
        this.notifications = this.notifications.map(n => n.notificationId === notification.notificationId ? { ...n, isRead: true } : n);
        this.groupNotifications();
        this.notificationsPoll.applyMarkAsRead();
        this.redirectToNotification(notification);
      },
      error: (error) => {
        console.error('❌ Error marking notification as read:', error);
        this.actionFeedback.showError('Could not mark notification as read.');
      },
      complete: () => {
        this.isMarkingAsRead.set(false);
      }
    });
  }

  /** Prefer navigation JSON; fall back to contextUrl and entity fields when JSON is missing. */
  private redirectToNotification(notification: NotificationModel): void {
    const tenantId = extractTenantIdFromRoute(this.router) ?? this.router.url.match(/\/([^/]+)\//)?.[1] ?? null;

    if (!tenantId) {
      console.error('❌ Could not redirect to notification', notification);
      this.actionFeedback.showError('Something went wrong, could not redirect to the notification');
      return;
    }

    const ok = this.notificationRedirect.navigateToNotification(notification, tenantId);
    if (!ok) {
      console.error('❌ Could not redirect to notification', notification);
      this.actionFeedback.showError('Something went wrong, could not redirect to the notification');
    }

    this.notificationMenu?.hide();
  }

}
